import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Input } from '../components/ui';
import { projectsApi, clearToken } from '../api/client';
import { useAppContext } from '../store/AppContext';
import type { Project } from '../api/types';

const PROJECT_COLORS = [
  '#4F46E5', '#059669', '#DC2626', '#D97706', '#7C3AED', '#0891B2',
];

function colorForProject(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return PROJECT_COLORS[Math.abs(hash) % PROJECT_COLORS.length];
}

interface Props {
  onSelectProject: (id: string) => void;
  onLogout: () => void;
}

export const ProjectsPage: React.FC<Props> = ({ onSelectProject, onLogout }) => {
  const { user, projects, setProjects, addProject } = useAppContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    projectsApi.list().then((res) => {
      if (cancelled) return;
      if (res.ok) {
        setProjects(Array.isArray(res.data) ? res.data : (res.data as { items: typeof res.data }).items ?? []);
      } else {
        setError(res.error.message);
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [setProjects]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    setCreateError('');
    const res = await projectsApi.create(newName.trim(), newDesc.trim() || undefined);
    setCreating(false);
    if (res.ok) {
      addProject(res.data);
      setShowCreate(false);
      setNewName('');
      setNewDesc('');
    } else {
      setCreateError(res.error.message);
    }
  };

  const handleLogout = () => {
    clearToken();
    onLogout();
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)' }}>
      {/* Header */}
      <div
        style={{
          background: 'white',
          borderBottom: '1px solid var(--border)',
          padding: '0 32px',
          height: 56,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: 'var(--primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 800,
            fontSize: 13,
          }}
        >
          BLS
        </div>
        <span style={{ fontWeight: 700, fontSize: 15 }}>better_local_slock</span>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {user?.displayName}
        </span>
        <Button variant="ghost" size="sm" onClick={handleLogout}>
          Sign out
        </Button>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '40px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 28 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, flex: 1 }}>Projects</h1>
          <Button onClick={() => setShowCreate(true)}>+ New Project</Button>
        </div>

        {loading && (
          <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 48 }}>
            Loading projects…
          </div>
        )}

        {error && (
          <div
            style={{
              background: '#FEE2E2',
              color: '#991B1B',
              padding: '12px 16px',
              borderRadius: 'var(--radius)',
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        {!loading && projects.length === 0 && !error && (
          <div
            style={{
              textAlign: 'center',
              padding: 64,
              color: 'var(--text-secondary)',
              background: 'white',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border)',
            }}
          >
            <div style={{ fontSize: 40, marginBottom: 12 }}>📁</div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>No projects yet</div>
            <div style={{ fontSize: 13, marginBottom: 20 }}>
              Create your first project to get started
            </div>
            <Button onClick={() => setShowCreate(true)}>Create project</Button>
          </div>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 16,
          }}
        >
          {projects.map((project: Project) => (
            <ProjectCard
              key={project.id}
              project={project}
              color={colorForProject(project.name)}
              onClick={() => onSelectProject(project.id)}
            />
          ))}
        </div>
      </div>

      {/* Create Dialog */}
      {showCreate && (
        <CreateProjectDialog
          onClose={() => { setShowCreate(false); setCreateError(''); }}
          onCreate={handleCreate}
          name={newName}
          setName={setNewName}
          desc={newDesc}
          setDesc={setNewDesc}
          creating={creating}
          error={createError}
        />
      )}
    </div>
  );
};

function ProjectCard({
  project,
  color,
  onClick,
}: {
  project: Project;
  color: string;
  onClick: () => void;
}) {
  const initials = project.name
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div
      onClick={onClick}
      style={{
        background: 'white',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 20,
        cursor: 'pointer',
        transition: 'box-shadow 0.15s, transform 0.15s',
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.borderColor = 'var(--primary)';
        el.style.boxShadow = '0 4px 16px rgba(79,70,229,0.12)';
        el.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.borderColor = 'var(--border)';
        el.style.boxShadow = 'none';
        el.style.transform = 'none';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 700,
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          {initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: 15,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {project.name}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {new Date(project.createdAt).toLocaleDateString()}
          </div>
        </div>
      </div>
      {project.description && (
        <p
          style={{
            fontSize: 13,
            color: 'var(--text-secondary)',
            lineHeight: 1.4,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {project.description}
        </p>
      )}
    </div>
  );
}

function CreateProjectDialog({
  onClose,
  onCreate,
  name,
  setName,
  desc,
  setDesc,
  creating,
  error,
}: {
  onClose: () => void;
  onCreate: (e: React.FormEvent) => Promise<void>;
  name: string;
  setName: (v: string) => void;
  desc: string;
  setDesc: (v: string) => void;
  creating: boolean;
  error: string;
}) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 'var(--radius-lg)',
          padding: 28,
          width: '100%',
          maxWidth: 440,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ flex: 1, fontSize: 17, fontWeight: 700 }}>New Project</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 18,
              color: 'var(--text-muted)',
            }}
          >
            ✕
          </button>
        </div>
        <form onSubmit={onCreate} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Input
            label="Project name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="My Awesome Project"
            required
            autoFocus
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: 13, fontWeight: 500 }}>Description (optional)</label>
            <textarea
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="What's this project about?"
              rows={3}
              style={{
                padding: '9px 12px',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                fontSize: 14,
                outline: 'none',
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
          </div>
          {error && (
            <div
              style={{
                background: '#FEE2E2',
                color: '#991B1B',
                padding: '8px 12px',
                borderRadius: 'var(--radius)',
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={creating || !name.trim()}>
              {creating ? 'Creating…' : 'Create project'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
